import { RestError } from '@azure/core-http';
import { CarrierDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { castDraft } from 'immer';
import { useImmer } from 'use-immer';
import { QueryKeys } from '../../../../../core/queries/query-keys.constants';
import { useMutationGrantServicing } from '../../../carrier-organisations.queries';
import { useMutationCreateCarrier, useQueryGetCarrierHierarchy } from '../../../carriers.queries';
import { DEFAULT_SERVICING_PERMISSION } from '../../../logic/add-carrier-form';
import { CarrierFormState } from '../../../details/actions/carrier-form-fields.component';

export interface AddCarrierActionProps {
  projectID: string;
  onAbort(): void;
  onSuccess(carrier: CarrierDto): void;
}

const NO_POOL = new Error('This project sits under no network pool, so carriers cannot be added here.');

export function useAddCarrierAction({ projectID, onAbort, onSuccess }: AddCarrierActionProps) {
  const [state, setState] = useImmer<{ saving: boolean; error?: RestError | Error }>({ saving: false });

  const { control, handleSubmit, setValue, watch } = useForm<CarrierFormState>({
    defaultValues: { name: '', shortName: '', image: '' }
  });

  // Asked on open, not by the list, as a network's hierarchy lists its whole pool.
  const hierarchy = useQueryGetCarrierHierarchy(projectID);
  const poolContainerID = hierarchy.data?.poolContainerID;
  // The network, as only a network's hierarchy has a pool.
  const networkID = hierarchy.data?.anchorProject.id;

  const createMutation = useMutationCreateCarrier();
  const grantServicing = useMutationGrantServicing();
  const queryClient = useQueryClient();

  const handlers = {
    onCancel: onAbort,
    async onSubmit(form: CarrierFormState) {
      if (!poolContainerID || !networkID) {
        return;
      }

      setState((draft) => {
        draft.saving = true;
      });

      try {
        const carrier = await createMutation.mutateAsync({
          parentGroupID: poolContainerID,
          name: form.name.trim(),
          shortName: form.shortName.trim() || undefined,
          image: form.image || undefined
        });

        // Carriers are listed by servicing, so one with none would be found nowhere. A failed grant
        // still opens the carrier, whose networks can be assigned there.
        await grantServicing
          .mutateAsync({
            groupID: carrier.id,
            grant: { projectID: networkID, permissions: [DEFAULT_SERVICING_PERMISSION] }
          })
          .catch(() => undefined);

        queryClient.invalidateQueries(QueryKeys.CARRIERS);
        onSuccess(carrier);
      } catch (error) {
        setState((draft) => {
          draft.saving = false;
          draft.error = castDraft(error as RestError);
        });
      }
    }
  };

  return {
    state,
    loading: hierarchy.isLoading,
    error: state.error ?? hierarchy.error ?? (hierarchy.isSuccess && !poolContainerID ? NO_POOL : undefined),
    handlers,
    control,
    handleSubmit,
    image: watch('image'),
    onImageUploaded: (url: string) => setValue('image', url, { shouldDirty: true })
  } as const;
}
